import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { getQaSelector } from '../../../../../utils/common'
import Button from '../../../../Button'
import { PageEmptyState } from '../../../../PageEmptyState'
import { Status, STATUS_SIZE_20 } from '../../../../Status'
import { Table } from '../../../../Table'
import { TablePagination } from '../../../../TablePagination'
import Text from '../../../../Text'
import { useApplicantProfileContext } from '../../RentalPortalApplicantProfile.hooks'
import { ApplicantProfileDocumentsRow } from '../ApplicantProfileDocumentsRow'
import {
  APPLICANT_DOCUMENT_COLUMNS_CONFIG,
  APPLICANT_PROFILE_DOCUMENTS_OUTSTANDING_TEXT_COLOR,
  APPLICANT_PROFILE_DOCUMENTS_SKELETON_ROWS,
  QA_NAME_APPLICANT_PROFILE_DOCUMENTS,
  QA_NAME_APPLICANT_PROFILE_DOCUMENTS_EMPTY,
  QA_NAME_APPLICANT_PROFILE_DOCUMENTS_OUTSTANDING,
  QA_NAME_APPLICANT_PROFILE_DOCUMENTS_OUTSTANDING_ITEM,
  QA_NAME_APPLICANT_PROFILE_DOCUMENTS_OUTSTANDING_MORE,
  QA_NAME_APPLICANT_PROFILE_DOCUMENTS_RETRY,
  QA_NAME_APPLICANT_PROFILE_DOCUMENTS_TABLE,
} from '../RentalPortalApplicantProfileDocuments.constants'
import { useApplicantProfileDocuments } from '../RentalPortalApplicantProfileDocuments.hooks'
import {
  getApplicantDocumentsEmptyState,
  getApplicantDocumentsState,
  getIsApplicantDocumentsEmpty,
  getIsApplicantDocumentsError,
} from '../RentalPortalApplicantProfileDocuments.utils'

import * as styles from './ApplicantProfileDocumentsMockTable.module.css'

import DocumentIcon from '../../../../../icons/document.svg'
import RefreshIcon from '../../../../../icons/refresh.svg'
import RentalPortalIcon from '../../../../../icons/rental-portal.svg'

function ApplicantProfileDocumentsMockTable() {
  const { t } = useTranslation()

  const { applicantData } = useApplicantProfileContext()

  const containerRef = useRef(null)

  const {
    handleRefresh,
    handleSetPagination,
    hasError,
    isInitiated,
    isLoading,
    items,
    pagination,
  } = useApplicantProfileDocuments({ applicantId: applicantData?.id })

  const state = getApplicantDocumentsState({
    documents: items,
    hasError,
    isInitiated,
  })

  const emptyState = getApplicantDocumentsEmptyState(applicantData)

  return (
    <div
      {...getQaSelector({ value: QA_NAME_APPLICANT_PROFILE_DOCUMENTS })}
      className={styles.ApplicantProfileDocumentsMockTable}
      ref={containerRef}
    >
      {getIsApplicantDocumentsError(state) ? (
        <PageEmptyState EntityIcon={RentalPortalIcon}>
          <Text isSecondary>
            {t('RentalPortal.ApplicantProfileDocumentsLoadErrorKey')}
          </Text>
          <Button.Flat
            beforeIconProps={{ Icon: RefreshIcon }}
            isLoading={isLoading}
            qaName={QA_NAME_APPLICANT_PROFILE_DOCUMENTS_RETRY}
            text={t('RentalPortal.ListingRetryBtnKey')}
            onClick={handleRefresh}
          />
        </PageEmptyState>
      ) : getIsApplicantDocumentsEmpty(state) ? (
        <div
          {...getQaSelector({
            value: QA_NAME_APPLICANT_PROFILE_DOCUMENTS_EMPTY,
          })}
          className={styles.emptyState}
        >
          <PageEmptyState EntityIcon={DocumentIcon}>
            <Text isSecondary>{t(emptyState.textKey)}</Text>
            {emptyState.outstanding.length > 0 ? (
              <div
                {...getQaSelector({
                  value: QA_NAME_APPLICANT_PROFILE_DOCUMENTS_OUTSTANDING,
                })}
                className={styles.outstanding}
              >
                {emptyState.outstanding.map(row => (
                  <Status
                    height={STATUS_SIZE_20}
                    key={row.key}
                    qaName={
                      QA_NAME_APPLICANT_PROFILE_DOCUMENTS_OUTSTANDING_ITEM
                    }
                    text={row.type_label}
                    textColor={
                      APPLICANT_PROFILE_DOCUMENTS_OUTSTANDING_TEXT_COLOR
                    }
                  />
                ))}
                {emptyState.hiddenCount > 0 ? (
                  <Text
                    isSecondary
                    qaName={
                      QA_NAME_APPLICANT_PROFILE_DOCUMENTS_OUTSTANDING_MORE
                    }
                  >
                    {t('RentalPortal.ListingBoardMoreCountKey', {
                      count: emptyState.hiddenCount,
                    })}
                  </Text>
                ) : null}
              </div>
            ) : null}
          </PageEmptyState>
        </div>
      ) : (
        <>
          <Table
            TableRow={ApplicantProfileDocumentsRow}
            actionsColumnWidth={0}
            className={styles.table}
            columns={APPLICANT_DOCUMENT_COLUMNS_CONFIG}
            containerRef={containerRef}
            context={QA_NAME_APPLICANT_PROFILE_DOCUMENTS_TABLE}
            headerRowProps={{ className: styles.row, resizable: false }}
            isLoading={isLoading}
            items={items}
            skeletonProps={{ withCheckbox: false }}
            skeletonRows={APPLICANT_PROFILE_DOCUMENTS_SKELETON_ROWS}
          />
          <TablePagination
            responsive
            containerRef={containerRef}
            isSkeleton={!isInitiated}
            value={pagination}
            onChange={handleSetPagination}
          />
        </>
      )}
    </div>
  )
}

export default ApplicantProfileDocumentsMockTable
